import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarRange, Zap } from "lucide-react";
import PayrollReadinessGate from "../../components/setup/PayrollReadinessGate";
import PayPeriods from "./PayPeriods";
import ProcessPayroll from "./PayrollProcess";
import "./payrollProcessHub.css";

const TABS = [
  {
    id: "periods",
    label: "Payroll ID",
    shortLabel: "Payroll ID",
    icon: CalendarRange,
  },
  {
    id: "process",
    label: "Process Payroll",
    shortLabel: "Process Payroll",
    icon: Zap,
  },
];

function resolveTab(raw) {
  const value = String(raw || "").toLowerCase();
  if (value === "process" || value === "payroll" || value === "run") return "process";
  return "periods";
}

/**
 * Classic bw-page payroll workspace.
 * Default view: Payroll ID (pay periods). Top switch opens Process Payroll.
 */
export default function PayrollProcessHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = useMemo(
    () => resolveTab(searchParams.get("tab")),
    [searchParams]
  );

  // Landing on /payroll-process with no tab → Payroll ID (periods)
  useEffect(() => {
    if (!searchParams.get("tab")) {
      setSearchParams({ tab: "periods" }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const setTab = useCallback(
    (next) => {
      const id = resolveTab(next);
      if (id === activeTab) return;
      // Push (not replace) so the page-level Back returns to the previous tab.
      setSearchParams(id === "process" ? { tab: "process" } : { tab: "periods" });
    },
    [setSearchParams, activeTab]
  );

  // Start every visit (and every tab switch) at the top of the page.
  const rootRef = useRef(null);
  useLayoutEffect(() => {
    const scroller = rootRef.current?.closest(".content-area");
    if (scroller) scroller.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [activeTab]);

  return (
    <div ref={rootRef} className="bw-page bg-gray-50 min-h-screen pp-hub">
      <header className="bw-page-header pp-hub-header">
        <div className="pp-hub-header__row relative z-[1]">
          <div
            className="pp-hub-top-switch"
            role="tablist"
            aria-label="Payroll ID and pay run"
          >
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const selected = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-controls={`pp-hub-panel-${tab.id}`}
                  id={`pp-hub-tab-${tab.id}`}
                  className={`pp-hub-top-switch__btn${selected ? " is-active" : ""}`}
                  onClick={() => setTab(tab.id)}
                >
                  <Icon size={15} strokeWidth={2.25} aria-hidden />
                  <span>{tab.shortLabel}</span>
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <div className="pp-hub-body">
        <div
          className="pp-hub-panel"
          role="tabpanel"
          id={`pp-hub-panel-${activeTab}`}
          aria-labelledby={`pp-hub-tab-${activeTab}`}
        >
          {activeTab === "periods" ? (
            <PayPeriods embedded />
          ) : (
            <PayrollReadinessGate>
              <ProcessPayroll embedded />
            </PayrollReadinessGate>
          )}
        </div>
      </div>
    </div>
  );
}
